import { Loading } from '@shared/components';
import { type PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState } from 'react';

import { useGetItemSetQuestionConditionsData } from '../../hooks';
import { type BaseQuestionConditionsContextType } from '../../types';
import { AddItemSetQuestionConditionsFormProvider } from '../add-item-set-question-conditions-form-provider';

type AddItemSetQuestionConditionsContextType = BaseQuestionConditionsContextType;

export const AddItemSetQuestionConditionsContext = createContext<
  AddItemSetQuestionConditionsContextType | undefined
>(undefined);

interface AddItemSetQuestionConditionsProviderProps extends PropsWithChildren {
  chapterId: string;
}

export function AddItemSetQuestionConditionsProvider(
  props: Readonly<AddItemSetQuestionConditionsProviderProps>,
) {
  const { children, chapterId } = props;
  const [selectedQuestionSize, setSelectedQuestionSize] = useState<number | undefined>(undefined);

  const { structures, chapterData, levelItemsWithLevel, checkExistOnStructures, isLoading } =
    useGetItemSetQuestionConditionsData({
      chapterId,
      questionSize: selectedQuestionSize,
    });

  const value: AddItemSetQuestionConditionsContextType = useMemo(
    () => ({
      chapterId,
      chapterData,
      levelItemsWithLevel,
      structures,
      checkExistOnStructures,
    }),
    [chapterId, chapterData, levelItemsWithLevel, structures, checkExistOnStructures],
  );

  if (isLoading) {
    return (
      <Loading
        sx={{
          height: '100%',
          minHeight: '200px',
        }}
      />
    );
  }

  return (
    <AddItemSetQuestionConditionsContext.Provider value={value}>
      <AddItemSetQuestionConditionsFormProvider onQuestionSizeChange={setSelectedQuestionSize}>
        {children}
      </AddItemSetQuestionConditionsFormProvider>
    </AddItemSetQuestionConditionsContext.Provider>
  );
}
